'use client';

import { c as _c } from "react/compiler-runtime";
import type { MessageListOutput } from '@/orpc/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useListMessages } from '@/hooks/queries/message.query';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useSession } from '@/lib/auth-client';
import { DeleteButton } from './delete-button';
import { MessagesLoader } from './messages-loader';
export function Messages() {
  const $ = _c(28);
  const {
    data,
    isSuccess,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useListMessages();
  const t = useTranslations();
  const {
    ref,
    inView
  } = useInView();
  let t0;
  let t1;
  if ($[0] !== fetchNextPage || $[1] !== hasNextPage || $[2] !== inView) {
    t0 = () => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    };
    t1 = [fetchNextPage, hasNextPage, inView];
    $[0] = fetchNextPage;
    $[1] = hasNextPage;
    $[2] = inView;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== data || $[6] !== isSuccess) {
    t2 = isSuccess && data.pages.every(_temp);
    $[5] = data;
    $[6] = isSuccess;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const noMessages = t2;
  let t3;
  if ($[8] !== data || $[9] !== isSuccess) {
    t3 = isSuccess && data.pages.map(_temp3);
    $[8] = data;
    $[9] = isSuccess;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isFetchingNextPage || $[12] !== isLoading) {
    t4 = (isLoading || isFetchingNextPage) && <MessagesLoader />;
    $[11] = isFetchingNextPage;
    $[12] = isLoading;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== ref) {
    t5 = <span ref={ref} className="invisible" />;
    $[14] = ref;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== isError || $[17] !== t) {
    t6 = isError && <div className="flex min-h-24 items-center justify-center"><p className="text-sm text-muted-foreground">{t("error.failed-to-load-messages")}</p></div>;
    $[16] = isError;
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== noMessages || $[20] !== t) {
    t7 = noMessages && <div className="flex min-h-24 items-center justify-center"><p className="text-sm text-muted-foreground">{t("guestbook.no-messages")}</p></div>;
    $[19] = noMessages;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <div className="flex flex-col gap-4" data-testid="guestbook-messages-list">{t3}{t4}{t5}{t6}{t7}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
function _temp3(page_0) {
  return page_0.messages.map(_temp2);
}
function _temp2(message) {
  return <Message key={message.id} message={message} />;
}
function _temp(page) {
  return page.messages.length === 0;
}
type MessageProps = {
  message: MessageListOutput['messages'][number];
};
function Message(props) {
  const $ = _c(25);
  const {
    message
  } = props;
  const {
    data: session
  } = useSession();
  const isAuthor = message.userId === session?.user.id;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      formatName: "long"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const formattedDate = useFormattedDate(message.createdAt, t0);
  const t1 = `message-${message.id}`;
  let t2;
  if ($[1] !== message.user.image || $[2] !== message.user.name || $[3] !== message.userId) {
    t2 = <UserAvatar id={message.userId} name={message.user.name} image={message.user.image} size="lg" />;
    $[1] = message.user.image;
    $[2] = message.user.name;
    $[3] = message.userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== message.user.name) {
    t3 = <div>{message.user.name}</div>;
    $[5] = message.user.name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = formattedDate ?? "--";
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="text-xs text-muted-foreground">{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="flex flex-col justify-center gap-px text-sm">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <div className="mb-3 flex gap-3">{t2}{t6}</div>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== message.body) {
    t8 = <div data-posthog-mask={true} className="pl-13 wrap-break-word">{message.body}</div>;
    $[15] = message.body;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isAuthor || $[18] !== message) {
    t9 = isAuthor && <DeleteButton message={message} />;
    $[17] = isAuthor;
    $[18] = message;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = <div className="rounded-xl border p-4" data-testid={t1}>{t7}{t8}{t9}</div>;
    $[20] = t1;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}